import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { TestModeBanner } from './TestModeBanner';

interface TestModeCardContainerProps {
  isTest: boolean;
  children: ReactNode;
  variant?: 'default' | 'prominent' | 'subtle';
  bannerPosition?: 'top-right' | 'top-full' | 'bottom';
  className?: string;
}

const TestModeCardContainer = ({
  isTest,
  children,
  variant = 'default',
  bannerPosition = 'top-right',
  className,
}: TestModeCardContainerProps) => {
  if (!isTest) {
    return <div className={className}>{children}</div>;
  }

  const containerClasses = {
    default:
      'border-2 border-dashed border-yellow-300 rounded-lg p-3 bg-yellow-50/30',
    prominent:
      'border-2 border-dashed border-amber-400 rounded-lg p-4 bg-amber-50/40 shadow-sm',
    subtle:
      'border border-dashed border-yellow-200 rounded-lg p-3 bg-yellow-25/20',
  };

  const bannerVariantMap = {
    default: 'compact' as const,
    prominent: 'prominent' as const,
    subtle: 'compact' as const,
  };

  const renderBanner = () => (
    <TestModeBanner
      isTest={isTest}
      variant={bannerVariantMap[variant]}
      showDescription={variant === 'prominent'}
    />
  );

  return (
    <div className={cn('relative', containerClasses[variant], className)}>
      {/* Top-full banner spans the full width */}
      {bannerPosition === 'top-full' && (
        <div className="mb-3 -mt-3 -mx-3 p-3 border-b border-yellow-300/50">
          {renderBanner()}
        </div>
      )}

      {/* Top-right positioned banner */}
      {bannerPosition === 'top-right' && (
        <div className="absolute top-2 right-2 z-10">{renderBanner()}</div>
      )}

      {/* Content with appropriate padding based on banner position */}
      <div
        className={cn(
          bannerPosition === 'top-right' && 'pr-24',
          bannerPosition === 'top-full' && 'pt-0',
        )}
      >
        {children}
      </div>

      {/* Bottom banner */}
      {bannerPosition === 'bottom' && (
        <div className="mt-3 -mb-3 -mx-3 p-3 border-t border-yellow-300/50">
          {renderBanner()}
        </div>
      )}
    </div>
  );
};

export { TestModeCardContainer };
